import { css } from '@emotion/react';
import { useState } from 'react';

import IconChevronDown from '~icons/ci/chevron-down';

const styles = {
	container: css({
		padding: '1rem',
	}),
	successSection: css({
		marginBottom: '1.5rem',
	}),
	successTitle: css({
		color: '#10b981',
		marginBottom: '0.5rem',
	}),
	successContainer: css({
		backgroundColor: '#ecfdf5',
		border: '1px solid #a7f3d0',
		borderRadius: '0.375rem',
		padding: '0.75rem',
	}),
	successItem: css({
		fontSize: '0.875rem',
	}),
	errorTitle: css({
		color: '#ef4444',
		marginBottom: '0.5rem',
	}),
	errorCard: css({
		backgroundColor: '#fef2f2',
		border: '1px solid #fecaca',
		borderRadius: '0.375rem',
		marginBottom: '0.75rem',
		overflow: 'hidden',
	}),
	errorHeader: css({
		padding: '0.75rem',
		display: 'flex',
		justifyContent: 'space-between',
		alignItems: 'center',
	}),
	errorHeaderClickable: css({
		cursor: 'pointer',
	}),
	errorHeaderExpanded: css({
		borderBottom: '1px solid #fecaca',
	}),
	errorId: css({
		fontSize: '0.875rem',
		fontWeight: 600,
	}),
	errorCount: css({
		marginLeft: '0.5rem',
		fontSize: '0.875rem',
		color: '#6b7280',
	}),
	chevronIcon: css({
		fontSize: '0.75rem',
		color: '#6b7280',
		transition: 'transform 0.2s',
	}),
	chevronExpanded: css({
		transform: 'rotate(180deg)',
	}),
	errorContent: css({
		padding: '0.75rem',
	}),
	errorMessage: css({
		fontSize: '0.875rem',
		color: '#dc2626',
		fontWeight: 500,
		marginBottom: '0.25rem',
	}),
	errorDetails: css({
		backgroundColor: '#ffffff',
		border: '1px solid #e5e7eb',
		borderRadius: '0.25rem',
		padding: '0.5rem',
		marginTop: '0.5rem',
	}),
	errorDetailsPre: css({
		fontSize: '0.75rem',
		color: '#374151',
		margin: 0,
		whiteSpace: 'pre-wrap',
		wordBreak: 'break-word',
	}),
	noResults: css({
		padding: '1rem',
		backgroundColor: '#f9fafb',
		border: '1px solid #e5e7eb',
		borderRadius: '0.375rem',
		textAlign: 'center',
		color: '#6b7280',
	}),
};

export interface HistoryTemplateUploadError {
	message: string;
	errorJson?: Record<string, unknown>;
}

export interface HistoryTemplateUploadResult {
	id: string;
	isSuccess: boolean;
	errors: HistoryTemplateUploadError[];
}

export interface HistoryTemplateUploadProps {
	results: HistoryTemplateUploadResult[];
}

export function HistoryTemplateUpload({ results }: HistoryTemplateUploadProps) {
	const [expandedErrors, setExpandedErrors] = useState<Set<string>>(new Set());

	const toggleErrorExpansion = (avbId: string) => {
		const newExpanded = new Set(expandedErrors);
		if (newExpanded.has(avbId)) {
			newExpanded.delete(avbId);
		} else {
			newExpanded.add(avbId);
		}
		setExpandedErrors(newExpanded);
	};

	// Check if error has meaningful details to expand
	const hasExpandableDetails = (error: HistoryTemplateUploadError) => {
		return error.errorJson && Object.keys(error.errorJson).length > 0;
	};

	// Split results into success and error groups
	const successfulResults = results.filter((result) => result.isSuccess);
	const errorResults = results.filter((result) => !result.isSuccess);

	// Group error results by avbId (though each should already be unique)
	const errorsById: Record<string, HistoryTemplateUploadError[]> = {};
	for (const result of errorResults) {
		if (result.errors.length > 0) {
			errorsById[result.id] = result.errors;
		}
	}

	const successIds = successfulResults.map((result) => result.id);

	return (
		<div css={styles.container}>
			{/* Success Section */}
			{successIds.length > 0 ? (
				<div css={styles.successSection}>
					<h4 css={styles.successTitle}>
						Successfully Uploaded ({successIds.length})
					</h4>
					<div css={styles.successContainer}>
						{successIds.map((id, index) => (
							<div
								key={id}
								css={[
									styles.successItem,
									{
										marginBottom: index < successIds.length - 1 ? '0.25rem' : 0,
									},
								]}
							>
								<span>{id}</span>
							</div>
						))}
					</div>
				</div>
			) : null}

			{/* Errors Section */}
			{Object.keys(errorsById).length > 0 ? (
				<div>
					<h4 css={styles.errorTitle}>
						Errors ({Object.keys(errorsById).length})
					</h4>
					{Object.entries(errorsById).map(([id, errors]) => {
						const hasAnyExpandableErrors =
							errors?.some(hasExpandableDetails) || false;

						return (
							<div key={id} css={styles.errorCard}>
								<div
									css={[
										styles.errorHeader,
										hasAnyExpandableErrors && styles.errorHeaderClickable,
										expandedErrors.has(id) && styles.errorHeaderExpanded,
									]}
									onClick={
										hasAnyExpandableErrors
											? () => toggleErrorExpansion(id)
											: undefined
									}
								>
									<div>
										<span css={styles.errorId}>{id}</span>
										<span css={styles.errorCount}>
											({errors?.length || 0} error
											{(errors?.length || 0) === 1 ? '' : 's'})
										</span>
									</div>
									{hasAnyExpandableErrors ? (
										<span
											css={[
												styles.chevronIcon,
												expandedErrors.has(id) && styles.chevronExpanded,
											]}
										>
											<IconChevronDown width={24} height={24} />
										</span>
									) : null}
								</div>

								{/* Error Messages */}
								<div css={styles.errorContent}>
									{errors?.map((error, index) => (
										<div
											key={index}
											css={{
												marginBottom:
													index < (errors?.length || 0) - 1 ? '0.5rem' : 0,
											}}
										>
											<div css={styles.errorMessage}>
												{error.message || 'Unknown error'}
											</div>

											{/* Expanded Error Details */}
											{expandedErrors.has(id) && hasExpandableDetails(error) ? (
												<div css={styles.errorDetails}>
													<pre css={styles.errorDetailsPre}>
														{JSON.stringify(error.errorJson, null, 2)}
													</pre>
												</div>
											) : null}
										</div>
									))}
								</div>
							</div>
						);
					})}
				</div>
			) : null}

			{/* No Results */}
			{successIds.length === 0 && Object.keys(errorsById).length === 0 ? (
				<div css={styles.noResults}>No results available</div>
			) : null}
		</div>
	);
}
